import { useEffect, useState } from "react";
import { PageHeader, SectionCard } from "@/components/Primitives";
import { Badge } from "@/components/ui/badge";
import { Code2, Key, Shield, BookOpen, Plus, Copy, Trash2, ShieldOff, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ENDPOINTS = [
  { method: "POST", path: "/api/public/v1/attendance", desc: "Ingest attendance record (X-API-Key)", external: true },
  { method: "POST", path: "/api/public/v1/tasks", desc: "Create a task (X-API-Key)", external: true },
  { method: "GET", path: "/api/employees", desc: "List all employees" },
  { method: "POST", path: "/api/employees", desc: "Create an employee (admin/manager)" },
  { method: "GET", path: "/api/employees/{id}/analytics", desc: "Full employee analytics" },
  { method: "GET", path: "/api/teams", desc: "List teams" },
  { method: "GET", path: "/api/projects", desc: "List projects" },
  { method: "GET", path: "/api/tasks", desc: "List tasks (filter by project, assignee, status)" },
  { method: "PATCH", path: "/api/tasks/{id}", desc: "Update task" },
  { method: "GET", path: "/api/attendance/summary", desc: "Attendance summary + trend" },
  { method: "GET", path: "/api/dashboard", desc: "Executive dashboard KPIs" },
  { method: "GET", path: "/api/workload", desc: "Workload analysis" },
  { method: "GET", path: "/api/risks", desc: "All risks + matrix" },
  { method: "GET", path: "/api/leaderboard?metric=productivity_score", desc: "Leaderboard by metric" },
  { method: "POST", path: "/api/ai/chat", desc: "Chat with the AI Manager" },
];

const AVAILABLE_SCOPES = [
  { id: "attendance:write", label: "Attendance · Write", desc: "Ingest attendance records" },
  { id: "tasks:write",      label: "Tasks · Write",      desc: "Create tasks from external tools" },
];

const METHOD_COLOR = {
  GET: "border-emerald-500/40 text-emerald-500",
  POST: "border-primary/40 text-primary",
  PATCH: "border-amber-500/40 text-amber-500",
  DELETE: "border-red-500/40 text-red-500",
};

const SAMPLE_CURL = `curl -X POST "$BACKEND/api/public/v1/attendance" \\
  -H "X-API-Key: pk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "emp_code": "EMP-2001",
    "date": "2026-09-12",
    "check_in": "09:15",
    "check_out": "18:20",
    "active_hours": 7.5,
    "idle_hours": 1.6,
    "mode": "wfh"
  }'

# You can also send the internal UUID via "employee_id".
# active_hours / idle_hours are optional decimal hours from your tracker.
# When active_hours is present it drives the Extra/Less calculation
# (WFH nominal 8-9h, WFO 7-8h). If omitted we fall back to check_out - check_in.
# mode: present | wfh | leave | absent  (aliases: "home" → wfh, "office" → present, etc.)`;

const SAMPLE_TASK_CURL = `curl -X POST "$BACKEND/api/public/v1/tasks" \\
  -H "X-API-Key: pk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "Investigate payment-gateway timeout",
    "project_code": "ACME-01",
    "emp_code": "EMP-2001",
    "priority": "high",
    "estimated_hours": 4,
    "due_date": "2026-02-28",
    "tags": ["bug", "payments"],
    "todos": [
      "Reproduce the 504 on staging",
      "Capture gateway logs",
      "Open PR with retry + backoff"
    ],
    "epic": "EPIC-02",
    "feature": "FEAT-05"
  }'

# You can send project_id (UUID) or project_code (e.g. "ACME-01").
# assignee is optional — use assignee_id (UUID) or emp_code.
# epic / feature accept either the UUID or the per-project identifier
# (EPIC-NN / FEAT-NN). Omit them for a standalone task.
# Duplicate (title, assignee) → 409 Conflict. Missing scope → 403.`;

function ApiKeysPanel() {
  const [keys, setKeys] = useState([]);
  const [newName, setNewName] = useState("");
  const [selectedScopes, setSelectedScopes] = useState(["attendance:write"]);
  const [creating, setCreating] = useState(false);
  const [reveal, setReveal] = useState(null); // { key, name }
  const [copied, setCopied] = useState(false);

  const load = () => api.get("/api-keys").then((r) => setKeys(r.data)).catch(() => setKeys([]));
  useEffect(() => { load(); }, []);

  const toggleScope = (sid) =>
    setSelectedScopes((prev) => (prev.includes(sid) ? prev.filter((s) => s !== sid) : [...prev, sid]));

  const create = async () => {
    if (!newName.trim()) return;
    if (!selectedScopes.length) return toast.error("Pick at least one scope");
    setCreating(true);
    try {
      const { data } = await api.post("/api-keys", { name: newName.trim(), scopes: selectedScopes });
      setNewName("");
      setSelectedScopes(["attendance:write"]);
      setReveal({ key: data.key, name: data.name });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create key");
    } finally { setCreating(false); }
  };

  const revoke = async (kid) => {
    if (!window.confirm("Revoke this key? Requests using it will start failing immediately.")) return;
    try { await api.post(`/api-keys/${kid}/revoke`); toast.success("Key revoked"); load(); }
    catch { toast.error("Failed"); }
  };

  const del = async (kid) => {
    if (!window.confirm("Delete this key record permanently?")) return;
    try { await api.delete(`/api-keys/${kid}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const copyKey = () => {
    if (!reveal?.key) return;
    navigator.clipboard.writeText(reveal.key).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <SectionCard title="API Keys" subtitle="For third-party tools pushing data via the public API">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Key name (e.g. HRMS Attendance Sync)"
          className="max-w-sm"
          data-testid="apikey-name"
          onKeyDown={(e) => { if (e.key === "Enter") create(); }}
        />
        <Button onClick={create} disabled={creating || !newName.trim()} data-testid="apikey-create">
          <Plus className="w-4 h-4 mr-1" />{creating ? "Creating…" : "Generate Key"}
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 mb-4" data-testid="apikey-scopes">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1">Scopes</span>
        {AVAILABLE_SCOPES.map((s) => {
          const on = selectedScopes.includes(s.id);
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => toggleScope(s.id)}
              title={s.desc}
              data-testid={`scope-${s.id.replace(":", "-")}`}
              className={`text-[11px] rounded-full border px-2.5 py-1 transition-colors ${
                on
                  ? "bg-primary/15 border-primary/40 text-primary"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {on ? "✓ " : ""}{s.label}
            </button>
          );
        })}
      </div>

      {keys.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center border border-dashed border-border/60 rounded-md">
          No API keys yet. Generate one to allow external tools to push data.
        </div>
      ) : (
        <div className="space-y-1">
          {keys.map((k) => (
            <div key={k.id} className="flex items-center gap-3 py-2 border-b border-border/40 last:border-0 text-sm" data-testid={`apikey-row-${k.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate flex items-center gap-1.5 flex-wrap">
                  <span>{k.name}</span>
                  {(k.scopes || []).map((s) => (
                    <Badge key={s} variant="outline" className="text-[9px] font-mono border-primary/30 text-primary">{s}</Badge>
                  ))}
                </div>
                <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                  <code className="font-mono">{k.prefix}…</code>
                  <span>·</span>
                  <span>created {(k.created_at || "").slice(0, 10)}</span>
                  {k.last_used_at && <><span>·</span><span>last used {(k.last_used_at || "").slice(0, 10)}</span></>}
                  {k.revoked_at && <Badge variant="outline" className="border-red-500/40 text-red-500 text-[10px]">revoked</Badge>}
                </div>
              </div>
              {!k.revoked_at && (
                <Button size="sm" variant="ghost" onClick={() => revoke(k.id)} title="Revoke" data-testid={`apikey-revoke-${k.id}`}>
                  <ShieldOff className="w-4 h-4" />
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => del(k.id)} title="Delete" data-testid={`apikey-delete-${k.id}`}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!reveal} onOpenChange={(v) => { if (!v) setReveal(null); }}>
        <DialogContent data-testid="apikey-reveal-dialog">
          <DialogHeader>
            <DialogTitle>Save this key now</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">
              This is the <b>only</b> time you'll see the full key. Store it in your third-party tool's config now.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 font-mono text-xs bg-accent px-3 py-2 rounded break-all">{reveal?.key}</code>
              <Button onClick={copyKey} variant="outline" size="sm" data-testid="apikey-copy">
                {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>
            <div className="text-xs text-muted-foreground">Name: <b>{reveal?.name}</b></div>
          </div>
          <DialogFooter>
            <Button onClick={() => setReveal(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}

export default function DeveloperApis() {
  return (
    <div className="space-y-6">
      <PageHeader
        testid="apis-header"
        title="Developer APIs"
        subtitle="REST endpoints · JWT + API Key auth · Rate limited"
        actions={<Button variant="outline"><BookOpen className="w-4 h-4 mr-2" />OpenAPI Docs</Button>}
      />

      <div className="grid md:grid-cols-3 gap-4">
        <div className="data-card p-4"><div className="flex items-center gap-2 mb-2"><Key className="w-4 h-4 text-primary" /><span className="font-medium text-sm">Auth</span></div><div className="text-xs text-muted-foreground">JWT bearer for the web app; <code className="font-mono">X-API-Key</code> for external tools.</div></div>
        <div className="data-card p-4"><div className="flex items-center gap-2 mb-2"><Shield className="w-4 h-4 text-primary" /><span className="font-medium text-sm">Rate Limiting</span></div><div className="text-xs text-muted-foreground">1000 req/min per key. Contact sales for higher.</div></div>
        <div className="data-card p-4"><div className="flex items-center gap-2 mb-2"><Code2 className="w-4 h-4 text-primary" /><span className="font-medium text-sm">SDKs</span></div><div className="text-xs text-muted-foreground">Python, TypeScript, Go coming soon.</div></div>
      </div>

      <ApiKeysPanel />

      <SectionCard title="Push Attendance (external)" subtitle="Use an API key with scope 'attendance:write'">
        <pre className="text-xs font-mono bg-accent/50 p-3 rounded overflow-x-auto whitespace-pre">{SAMPLE_CURL}</pre>
        <div className="text-xs text-muted-foreground mt-2">
          Duplicate (employee_id + date) → <code>409 Conflict</code>. Invalid mode → <code>400</code>. Missing/revoked key → <code>401</code>. Employee not found → <code>404</code>.
        </div>
      </SectionCard>

      <SectionCard title="Push Tasks (external)" subtitle="Use an API key with scope 'tasks:write'" testid="push-tasks-card">
        <pre className="text-xs font-mono bg-accent/50 p-3 rounded overflow-x-auto whitespace-pre" data-testid="task-curl-sample">{SAMPLE_TASK_CURL}</pre>
        <div className="text-xs text-muted-foreground mt-2">
          Duplicate (title + assignee) → <code>409 Conflict</code>. Project / assignee / epic / feature not found → <code>404</code>. Missing or wrong-scope key → <code>401/403</code>. Reporter on the created task is the admin who generated the API key.
        </div>
      </SectionCard>

      <SectionCard title="Endpoints">
        <div className="space-y-1">
          {ENDPOINTS.map((e, i) => (
            <div key={i} className="flex items-center gap-3 py-2 border-b border-border/40 last:border-0 text-sm">
              <Badge variant="outline" className={`w-16 justify-center font-mono text-[10px] ${METHOD_COLOR[e.method]}`}>{e.method}</Badge>
              <code className="text-xs font-mono text-primary">{e.path}</code>
              {e.external && <Badge variant="outline" className="text-[9px] border-amber-500/40 text-amber-500">X-API-Key</Badge>}
              <span className="text-muted-foreground ml-auto text-xs">{e.desc}</span>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}
